import showFREDialogCallout from '../components/FREDialogCallout';
import { logUsage } from 'owa-analytics';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lighted, LightningId, shouldShowLightningItem } from 'owa-lightning-v2';
import loc from 'owa-localize';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import {
    getDefaultShouldEventsEndEarly,
    getDefaultShouldShortenEventDuration,
} from 'owa-calendar-cloud-settings';
import {
    speedyMeetingsFREDialogTitle,
    speedyMeetingsFREDialogDescription,
    speedyMeetingsFREDialogOkButton,
    speedyMeetingsFREDialogCancelButton,
} from './showSpeedyMeetingsFREDialog.locstring.json';

/**
 * Show a FRE dialog to teach the users how to schedule shorter events by default
 * It will be shown only once after the user saves an event for which they had to type the start time or end time
 * And the start time / end time is not on the 00/15/30/45 min mark
 */
export default async function showSpeedyMeetingsFREDialog() {
    if (
        !shouldShowSpeedyMeetingsEndEarlyFREDialog() &&
        !shouldShowShortenEventsDurationFREDialog()
    ) {
        return;
    }

    const response = await confirm(
        loc(speedyMeetingsFREDialogTitle),
        loc(speedyMeetingsFREDialogDescription),
        false /* resolveImmediately */,
        {
            // Wait until dialog is dismissed to avoid focus interruption
            delayCallbackAfterAnimation: true,
            okText: loc(speedyMeetingsFREDialogOkButton),
            cancelText: loc(speedyMeetingsFREDialogCancelButton),
        }
    );

    let interactionType;

    if (response === DialogResponse.ok) {
        // Navigate to Events and invitations settings
        lazyMountAndShowFullOptions.importAndExecute('calendar', 'eventAndInvitations', 'event');
        interactionType = 'GoToSettingsClicked';

        // Show a callout and anchor it to the speedy meetings option
        const elementId = isFeatureEnabled('cal-mf-shortenEventsDuration')
            ? 'shortenEventsOption'
            : 'speedyMeetingsOption';
        showFREDialogCallout(() => document.getElementById(elementId));
    } else {
        interactionType = 'DismissClicked';
    }

    // End lightning
    const lightningId = isFeatureEnabled('cal-mf-shortenEventsDuration')
        ? LightningId.ShortenEventsDurationSetting
        : LightningId.SpeedyMeetingsEndEarlySetting;
    lighted(lightningId);

    logUsage('SpeedyMeetingsFREDialog', {
        interactionType: interactionType,
        isNewSetting: isFeatureEnabled('cal-mf-shortenEventsDuration'),
    });
}

/**
 * Show the speedy meetings - end early FRE dialog if:
 * - the feature is enabled and
 * - the dialog flight is enabled and
 * - the new feature is not enabled and
 * - the setting is not turned on and
 * - the user didn't see the dialog
 */
function shouldShowSpeedyMeetingsEndEarlyFREDialog(): boolean {
    return (
        isFeatureEnabled('cal-mf-shouldEventsEndEarlySetting') &&
        isFeatureEnabled('cal-mf-shouldEventsEndEarlyFREDialog') &&
        !isFeatureEnabled('cal-mf-shortenEventsDuration') &&
        !getDefaultShouldEventsEndEarly() &&
        shouldShowLightningItem(LightningId.SpeedyMeetingsEndEarlySetting)
    );
}

/**
 * Show the shorten events duration FRE dialog if:
 * - the feature is enabled and
 * - the setting is not turned on and
 * - the user didn't see the dialog
 */
function shouldShowShortenEventsDurationFREDialog(): boolean {
    return (
        isFeatureEnabled('cal-mf-shortenEventsDuration') &&
        !getDefaultShouldShortenEventDuration() &&
        shouldShowLightningItem(LightningId.ShortenEventsDurationSetting)
    );
}
